import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FiscalReminder } from "@/components/FiscalReminder";
import { Sparkles, FileText, CheckCircle2, XCircle, Send, Users, ArrowUpRight, Eye, Clock, TrendingUp, Wallet, Receipt, Camera, Database } from "lucide-react";

const STATUS = {
  draft: { label: "Borrador", cls: "bg-slate-100 text-slate-700" },
  sent: { label: "Enviado", cls: "bg-blue-100 text-blue-700" },
  accepted: { label: "Aceptado", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [acc, setAcc] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data));
    api.get("/dashboard/funnel").then((r) => setFunnel(r.data));
    api.get("/accounting/dashboard").then((r) => setAcc(r.data));
  }, []);

  if (!s || !funnel) return <div className="text-slate-500">Cargando…</div>;

  const ivaLiquidar = acc ? (Number(acc.income.iva) - Number(acc.expenses.tax)) : 0;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">Panel de control</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Resumen</h1>
        </div>
        <Link to="/generator" data-testid="cta-generate" className="btn-brand h-12 px-6 inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Generar presupuesto
        </Link>
      </div>

      <FiscalReminder />

      {/* Acciones rápidas: 3 tarjetones para usuarios no técnicos */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Link
          to="/generator"
          data-testid="quick-generate"
          className="group block bg-orange-600 text-white p-5 hover:bg-orange-700 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-white/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg">Crear presupuesto con IA</div>
              <div className="text-xs text-white/90">Describe el trabajo, la IA calcula precios</div>
            </div>
            <ArrowUpRight className="w-5 h-5 opacity-70 group-hover:opacity-100" />
          </div>
        </Link>

        <Link
          to="/files?upload=1"
          data-testid="quick-upload-invoice"
          className="group block bg-slate-900 text-white p-5 hover:bg-slate-800 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-white/10 flex items-center justify-center">
              <Camera className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg">Subir factura o ticket</div>
              <div className="text-xs text-white/70">La IA extrae proveedor, NIF y total</div>
            </div>
            <ArrowUpRight className="w-5 h-5 opacity-70 group-hover:opacity-100" />
          </div>
        </Link>

        <Link
          to="/files?upload=1&hint=bc3"
          data-testid="quick-import-bc3"
          className="group block bg-white border border-slate-200 p-5 hover:border-orange-600 hover:shadow-sm transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-orange-50 flex items-center justify-center">
              <Database className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg text-slate-900">Importar BC3</div>
              <div className="text-xs text-slate-500">Convierte .bc3 en presupuesto en 1 clic</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-orange-600" />
          </div>
        </Link>
      </div>

      {/* Accounting quick widget */}
      {acc && (
        <Link
          to="/accounting"
          data-testid="accounting-widget"
          className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-900 text-white hover:bg-slate-800 transition-colors group"
        >
          <div className="bg-slate-900 px-6 py-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-white/10 flex items-center justify-center">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <div className="kbar text-white/50">Beneficio del mes</div>
              <div className="font-display font-extrabold text-2xl tabular-nums" data-testid="widget-profit">
                {(acc.profit || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            </div>
          </div>
          <div className="bg-slate-900 px-6 py-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-white/10 flex items-center justify-center">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <div className="kbar text-white/50">IVA a liquidar</div>
              <div className={`font-display font-extrabold text-2xl tabular-nums ${ivaLiquidar > 0 ? "text-orange-400" : ""}`} data-testid="widget-iva">
                {ivaLiquidar.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            </div>
          </div>
          <div className="bg-slate-900 px-6 py-4 flex items-center justify-between gap-3">
            <div>
              <div className="kbar text-white/50">Ingresos / Gastos</div>
              <div className="font-display font-extrabold text-lg tabular-nums">
                <span className="text-green-400">{(acc.income.total || 0).toFixed(0)}</span>
                <span className="text-white/30 mx-2">·</span>
                <span className="text-red-400">{(acc.expenses.total || 0).toFixed(0)}</span>
                <span className="text-white/50 text-sm"> €</span>
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mt-8">
        <Stat icon={Send} label="Enviados" value={s.by_status.sent} sub={`${s.totals.sent.toFixed(0)} €`} testid="stat-sent" />
        <Stat icon={CheckCircle2} label="Aceptados" value={s.by_status.accepted} sub={`${s.totals.accepted.toFixed(0)} €`} accent testid="stat-accepted" />
        <Stat icon={XCircle} label="Rechazados" value={s.by_status.rejected} sub={`${s.totals.rejected.toFixed(0)} €`} testid="stat-rejected" />
        <Stat icon={FileText} label="Total" value={s.total_budgets} sub={`${s.accept_rate}% aceptación`} testid="stat-total" />
      </div>

      {/* Funnel */}
      <div className="mt-px border border-slate-200 bg-white p-6" data-testid="funnel-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="kbar">Embudo de conversión</div>
            <h2 className="font-display font-extrabold text-2xl mt-1">Cómo van tus presupuestos</h2>
          </div>
          <div className="flex gap-6 text-sm">
            <Metric icon={Eye} label="Vistas totales" value={funnel.total_views} testid="funnel-views" />
            <Metric icon={Clock} label="Tiempo medio respuesta" value={funnel.avg_response_hours > 0 ? `${funnel.avg_response_hours} h` : "—"} testid="funnel-avg-time" />
            <Metric icon={TrendingUp} label="Tasa aceptación" value={`${funnel.accept_rate}%`} testid="funnel-accept-rate" />
          </div>
        </div>

        <FunnelBars sent={funnel.sent} viewed={funnel.viewed} accepted={funnel.accepted} rejected={funnel.rejected} pending={funnel.pending} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-px">
        <div className="bg-white p-6">
          <Users className="w-6 h-6 text-slate-400" />
          <div className="mt-4 text-3xl font-display font-extrabold" data-testid="stat-clients">{s.clients_count}</div>
          <div className="text-sm text-slate-500">Clientes en cartera</div>
          <Link to="/clients" className="mt-3 inline-flex text-sm text-orange-600 font-semibold items-center gap-1">Ver clientes <ArrowUpRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="bg-white p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="kbar">Últimos presupuestos</div>
            <Link to="/budgets" className="text-sm text-orange-600 font-semibold">Ver todos</Link>
          </div>
          <div className="mt-4 divide-y divide-slate-200">
            {s.recent.length === 0 && <div className="text-sm text-slate-500 py-4">Aún no has creado presupuestos. <Link to="/generator" className="text-orange-600 font-semibold">Crea el primero →</Link></div>}
            {s.recent.map((b) => (
              <Link key={b.id} to={`/budgets/${b.id}`} className="flex items-center justify-between py-3 hover:bg-slate-50 -mx-3 px-3 transition-colors">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{b.ai_data?.title || b.work_type}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <span>{b.client_name} · {b.number}</span>
                    {b.views > 0 && <span className="inline-flex items-center gap-0.5 text-blue-600"><Eye className="w-3 h-3" /> {b.views}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold tabular-nums">{(b.ai_data?.total || 0).toFixed(0)} €</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${STATUS[b.status]?.cls}`}>{STATUS[b.status]?.label || b.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelBars({ sent, viewed, accepted, rejected, pending }) {
  const max = Math.max(sent, 1);
  const Stage = ({ count, label, color, sub, testid }) => {
    const pct = (count / max) * 100;
    return (
      <div data-testid={testid}>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-sm font-bold">{label}</span>
          <span className="text-xs text-slate-500">{sub}</span>
        </div>
        <div className="h-10 bg-slate-100 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 transition-all duration-700 flex items-center px-3 text-white font-bold text-sm" style={{ width: `${Math.max(pct, 6)}%`, background: color }}>
            {count}
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-3">
      <Stage testid="funnel-stage-sent" count={sent} label="Enviados al cliente" color="#475569" sub="Total enviados" />
      <Stage testid="funnel-stage-viewed" count={viewed} label="Vistos por el cliente" color="#2563EB" sub={sent ? `${Math.round((viewed / sent) * 100)}% lo abrió` : ""} />
      <Stage testid="funnel-stage-pending" count={pending} label="Pendiente de respuesta" color="#F59E0B" sub="" />
      <Stage testid="funnel-stage-accepted" count={accepted} label="Aceptados" color="#16A34A" sub={sent ? `${Math.round((accepted / sent) * 100)}% conversión` : ""} />
      <Stage testid="funnel-stage-rejected" count={rejected} label="Rechazados" color="#DC2626" sub="" />
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="text-right" data-testid={testid}>
      <div className="flex items-center justify-end gap-1 text-xs text-slate-500"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-display font-extrabold text-xl mt-0.5">{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className={`p-6 ${accent ? "bg-orange-600 text-white" : "bg-white"}`} data-testid={testid}>
      <Icon className={`w-6 h-6 ${accent ? "text-white/80" : "text-slate-400"}`} />
      <div className="mt-4 text-3xl font-display font-extrabold">{value}</div>
      <div className={`text-sm ${accent ? "text-white/90" : "text-slate-500"}`}>{label}</div>
      <div className={`text-xs mt-1 ${accent ? "text-white/80" : "text-slate-400"}`}>{sub}</div>
    </div>
  );
}
